import { QueryCache } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { extractApiError } from "../utils/formErrorHandler";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /**
       * Opt a query out of the global failure toast — for queries whose
       * component already renders a dedicated error state, or whose failure is
       * expected/handled locally. Mirrors the mutation `suppressErrorNotification`.
       */
      suppressErrorNotification?: boolean;
    };
  }
}

/** HTTP status off an axios-style error, if present. */
function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

function codeOf(error: unknown): string | undefined {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code;
  return typeof code === "string" ? code : undefined;
}

/**
 * A 403 the SERVER explained, keyed by its machine-readable `code`.
 *
 * Every other 403 keeps the generic "no access" line on purpose: the raw
 * server strings are written for developers ("MFA-provenance verification
 * required for this action") and often name internals. But the MFA gates
 * answer a question the user can act on, and without this the answer never
 * reached them — 5 `mfa: true` GET routes (admin legal packets/documents)
 * rendered one generic sentence no matter the cause, including "two-factor
 * is temporarily unavailable" during a Cognito outage.
 */
const EXPLAINED_403: Record<string, string> = {
  MFA_REQUIRED:
    "This page needs two-factor authentication. Sign out and back in with your authenticator app.",
  MFA_CHECK_UNAVAILABLE:
    "Two-factor verification is temporarily unavailable. Please try again shortly.",
};

/**
 * A request that was cancelled/aborted rather than a real failure. Axios cancel
 * tokens / AbortController produce `ERR_CANCELED`; DOM aborts produce
 * `AbortError`; TanStack's own cancellation produces `CancelledError`. None of
 * these has a `response`, so they'd otherwise be misread as "no connection".
 * (Genuine network/timeout failures use `ERR_NETWORK` / `ECONNABORTED` and are
 * deliberately NOT matched here, so real outages still toast.)
 */
function isAbortError(error: unknown): boolean {
  const e = error as { code?: string; name?: string } | null | undefined;
  return (
    e?.code === "ERR_CANCELED" ||
    e?.name === "CanceledError" ||
    e?.name === "CancelledError" ||
    e?.name === "AbortError"
  );
}

/**
 * True while the page is tearing down — a logout redirect, the 401 sign-out
 * path, or any hard `window.location` navigation. Such a navigation aborts the
 * in-flight XHRs at the browser level; their rejections carry no `response`, so
 * without this guard a normal logout flashes a false "check your connection"
 * toast. `pagehide`/`beforeunload` fire before those aborts settle; `pageshow`
 * resets the flag if the page is restored from the bfcache.
 */
let isUnloading = false;
let unloadListenersRegistered = false;

function registerUnloadListeners(): void {
  if (unloadListenersRegistered || typeof window === "undefined") return;
  unloadListenersRegistered = true;
  const markUnloading = () => {
    isUnloading = true;
  };
  window.addEventListener("pagehide", markUnloading);
  window.addEventListener("beforeunload", markUnloading);
  window.addEventListener("pageshow", () => {
    isUnloading = false;
  });
}

/**
 * Audit B-H1 — global safety net for failed reads.
 *
 * The app had a `MutationCache.onError` toast but no query equivalent, so a
 * failed list/detail GET rendered the component's EMPTY state — a med-pass nurse
 * saw "No medications due" when the request actually failed. This toasts the
 * silent-failure classes so a load error can never masquerade as "nothing here":
 *
 *  - 401 → skipped: the apiClient interceptor refreshes the token / redirects.
 *  - 404 → skipped: a genuine not-found; detail pages render their own state.
 *  - 403 / 5xx / network → toasted: these are the ones that otherwise vanish
 *    into an empty list. Pages should ALSO add an explicit `isError` branch for
 *    a proper retry UI (and to distinguish 403 "no access" from 404), but this
 *    guarantees no failure is fully silent.
 *
 * A short dedup window collapses the flood when several queries on one page fail
 * together during an outage.
 */
const DEDUP_WINDOW_MS = 4000;
const recentMessages = new Map<string, number>();

/**
 * Clear the toast dedup cache. FOR TESTS ONLY.
 *
 * `recentMessages` collapses identical toasts within DEDUP_WINDOW_MS, which is
 * right in the browser but survives between tests: a second test toasting the
 * same message inside the window gets silently swallowed and asserts "0 times".
 * It is also why mocking `Date.now` per-test is not enough — a clock that
 * restarts at 0 each test makes the elapsed delta NEGATIVE against entries
 * stamped by an earlier test, which reads as "duplicate" and swallows every
 * time.
 */
export function __resetErrorToastDedupeForTests(): void {
  recentMessages.clear();
}

function messageForStatus(error: unknown): string {
  const status = statusOf(error);
  if (status === 403) {
    return (
      EXPLAINED_403[codeOf(error) ?? ""] ??
      "You don't have access to this data, or it isn't available for the current facility."
    );
  }
  if (status && status >= 500) {
    return "Something went wrong loading this. Please try again.";
  }
  // No response → network/timeout.
  if (status === undefined) {
    return "Couldn't load — check your connection and try again.";
  }
  return extractApiError(error);
}

/**
 * The QueryCache `onError` handler, extracted so it can be unit-tested directly.
 * Toasts the silent-failure classes; stays quiet for opt-outs, auth/not-found
 * (handled elsewhere), and request cancellations / page tear-downs (not real
 * failures).
 */
export function notifyQueryError(
  error: unknown,
  query: { meta?: { suppressErrorNotification?: boolean } },
): void {
  if (query.meta?.suppressErrorNotification) return;

  // A request aborted by a logout/redirect navigation, or otherwise cancelled,
  // is not a connection failure — the page is going away. Suppress its toast.
  if (isUnloading || isAbortError(error)) return;

  const status = statusOf(error);
  // Handled elsewhere (auth interceptor) or legitimately page-owned.
  if (status === 401 || status === 404) return;

  const message = messageForStatus(error);

  // Collapse identical messages fired within the dedup window (e.g. five
  // queries on a page all 5xx during an outage → one toast, not five).
  const now = Date.now();
  const last = recentMessages.get(message);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return;
  // Prune stale entries first so the Map can't grow unbounded over a
  // long-lived session (distinct server error strings accumulate otherwise).
  for (const [key, ts] of recentMessages) {
    if (now - ts >= DEDUP_WINDOW_MS) recentMessages.delete(key);
  }
  recentMessages.set(message, now);

  notifications.show({
    title: "Couldn't load",
    message,
    color: "red",
  });
}

export function createAppQueryCache(): QueryCache {
  registerUnloadListeners();
  return new QueryCache({ onError: notifyQueryError });
}
